import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import TaskProvider, { useTask } from "context/admin/task";
import TaskViewProvider, { useTaskView } from "context/admin/task_view";
import { useTimeTracking } from "context/time-tracking";

import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import PlaceholderPage from "components/PlaceholderPage";
import TaskView from "page_components/shared/tasks/TaskView";
import {
  DeleteTaskModal,
  WebsiteInfoModal,
} from "page_components/shared/tasks/modals";
import { InputEdit } from "page_components/shared/tasks/TaskView/components";

import useResponsive from "libs/helpers/useResponsive";
import task_status_data from "libs/task_status_data.json";

import { ReactComponent as TrashIcon } from "assets/icons/new/trash.svg";

const TaskSingle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { max } = useResponsive();
  
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  const backPath = returnTo?.match(/^\/(webmasters|clients)\//)
    ? returnTo
    : `/tasks${location.search}`;

  const { updateTask } = useTask();
  const {
    data,
    refetch,
    optimisticUpdate,
    file_progress,
    sendMessage,
    editMessage,
    loading,
    error,
    task_close_confirmation,
    is_message_sending,
  } = useTaskView();
  const { current, timeTracking } = useTimeTracking();

  const [modalDelete, setModalDelete] = useState(false);
  const [modalWebsiteInfo, setModalWebsiteInfo] = useState(false);
  const [titleEdit, setTitleEdit] = useState(false);

  const status_data = task_status_data?.find(
    (item) => item?.value === data?.status,
  );
  const isCompletelyDisabled = status_data?.key === "canceled";

  const action_buttons = [
    !loading && !isCompletelyDisabled
      ? {
          label: t("Delete task"),
          icon: <TrashIcon />,
          variant: "error",
          onclick: () => setModalDelete(true),
        }
      : null,
    {
      type: "send_notification",
    },
  ].filter((item) => !!item);

  const trackingId = useRef(current?.task_id);

  useEffect(() => {
    if (current?.tasks?.[0]?._id) {
      trackingId.current = current.tasks[0]._id;
    } else {
      if (
        trackingId.current !== undefined &&
        data?._id !== undefined &&
        trackingId.current === data._id
      ) {
        refetch();

        trackingId.current = undefined;
      }
    }

    // eslint-disable-next-line
  }, [data, current]);

  useEffect(() => {
    if (error) navigate("/tasks");
  }, [error, navigate]);

  return (
    <DashboardContentLayout
      backMobile={() => navigate(backPath)}
      header={{
        label: {
          value: data?.title,
          children:
            titleEdit && !isCompletelyDisabled ? (
              <InputEdit
                initialValue={data?.title}
                onClose={() => setTitleEdit(false)}
                onSave={(value) => {
                  optimisticUpdate({ title: value });
                  updateTask({ title: value });
                  setTitleEdit(false);
                }}
              />
            ) : null,
          onClick: !isCompletelyDisabled ? () => setTitleEdit(true) : undefined,
        },
        description: {
          value: data?.website,
          onClick: () => setModalWebsiteInfo(true),
        },
        actions: !max.sm && !loading && (
          <p>
            {t("Created at")}{" "}
            <strong>{moment(data?.created_at).format("LL")}</strong>
          </p>
        ),
        async: true,
      }}
      loading={loading}
      actions={action_buttons}
      showActionsMobile
    >
      {!!!task_close_confirmation ? (
        <TaskView
          data={data}
          website={{
            name: data?.website,
            onClick: () => setModalWebsiteInfo(true),
          }}
          loading={loading}
          file_progress={file_progress}
          updateTask={updateTask}
          timeTracking={timeTracking}
          currentTimeTracking={current}
          sendMessage={sendMessage}
          editMessage={editMessage}
          isMessageSending={is_message_sending}
          isCompletelyDisabled={isCompletelyDisabled}
        />
      ) : (
        <PlaceholderPage
          title={t("The task has been finished!")}
          text={t(
            "We are glad we could help and would like to thank you for fruitful cooperation :)",
          )}
          button={{
            label: t("Manage your website"),
            href: "/websites",
          }}
        />
      )}

      <DeleteTaskModal
        title={data?.title}
        isOpen={modalDelete}
        onClose={() => setModalDelete(false)}
      />
      <WebsiteInfoModal
        id={data?.website_id}
        isOpen={modalWebsiteInfo}
        onClose={() => setModalWebsiteInfo(false)}
      />
    </DashboardContentLayout>
  );
};

const TaskSingleWrapper = () => (
  <TaskProvider>
    <TaskViewProvider>
      <TaskSingle />
    </TaskViewProvider>
  </TaskProvider>
);

export default TaskSingleWrapper;
